/**
 * CustomerReviewCard - Individual customer review card
 * Displays a single customer review with rating, title, comment, and verification badge
 */

import { useState, useRef, useEffect } from "react";
import type { FC } from "react";
import classNames from "classnames";
import { useLocale } from "@commercekit/remix";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import { StarRating } from "../StarRating";
import { formatCustomerName } from "../utils";

interface CustomerReviewCardProps {
  rating: number;
  comment: string;
  customerName?: string;
  date?: string;
  verified?: boolean;
  className?: string;
}

export const CustomerReviewCard: FC<CustomerReviewCardProps> = ({
  rating,
  comment,
  customerName,
  date,
  verified = true,
  className,
}) => {
  const { localeFullName } = useLocale();
  const { $pt } = usePrismicLabels();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  
  const displayName = formatCustomerName(customerName, localeFullName) || $pt("customer_review_anonymous");

  // Check if text is actually truncated
  // Using requestAnimationFrame to defer layout read and prevent forced reflow
  useEffect(() => {
    const element = textRef.current;
    if (element) {
      const checkTruncation = () => {
        setIsTruncated(element.scrollHeight > element.clientHeight);
      };
      
      const rafId = requestAnimationFrame(checkTruncation);
      return () => cancelAnimationFrame(rafId);
    }
  }, [comment]);

  return (
    <div
      className={classNames(
        "p-4 bg-white border position-relative customer_review_card_section d-flex flex-column h-100",
        className
      )}
    >
      <div className="d-flex align-items-center justify-content-between mb-3 customer_review_card_rating_section">
        {/* Star Rating */}
        <div>
          <StarRating rating={rating} size="md" />
        </div>
        {/* Verified Badge */}
        {verified && (
          <div className="d-flex align-items-center">
            <span
              className="d-inline-flex align-items-center p-2 bg-light text-sm customer_review_card_verified_badge"
            >
              <span className="customer_review_card_verified_badge_icon">
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.38333 9.35L10.3771 4.35625L9.38542 3.36458L5.38333 7.36667L3.36458 5.34792L2.37292 6.33958L5.38333 9.35ZM1.41667 12.75C1.02708 12.75 0.693576 12.6113 0.416146 12.3339C0.138715 12.0564 0 11.7229 0 11.3333V1.41667C0 1.02708 0.138715 0.693576 0.416146 0.416146C0.693576 0.138715 1.02708 0 1.41667 0H11.3333C11.7229 0 12.0564 0.138715 12.3339 0.416146C12.6113 0.693576 12.75 1.02708 12.75 1.41667V11.3333C12.75 11.7229 12.6113 12.0564 12.3339 12.3339C12.0564 12.6113 11.7229 12.75 11.3333 12.75H1.41667ZM1.41667 11.3333H11.3333V1.41667H1.41667V11.3333Z" fill="#1D1B20"/>
                </svg>
              </span>
              <p className="text-gray mb-0">{$pt("customer_review_verified_badge")}</p>
            </span>
          </div>
        )}
      </div>

      {/* Comment - with line clamp and show more functionality */}
      <div className="mb-3 flex-grow-1">
        <div className="position-relative">
          <p 
            ref={textRef}
            className={classNames(
              "text-gray customer_review_card_comment mb-0",
              {
                "customer_review_card_comment_truncated": !isExpanded,
                "customer_review_card_comment_truncated_with_padding": !isExpanded && isTruncated
              }
            )}
          >
            {comment}
          </p>
          {isTruncated && !isExpanded && (
            <button
              type="button"
              onClick={() => setIsExpanded(true)}
              className="text-primary p-0 border-0 bg-transparent position-absolute customer_review_card_show_more_button"
            >
              ... {$pt("customer_review_show_more")}
            </button>
          )}
        </div>
        {isExpanded && isTruncated && (
          <button
            type="button"
            onClick={() => setIsExpanded(false)}
            className="text-primary p-0 mt-1 border-0 bg-transparent text-sm cursor-pointer customer_review_card_show_less_button"
          >
            {$pt("customer_review_show_less")}
          </button>
        )}
      </div>

      {/* Footer with customer name and date - always at bottom */}
      <div className="align-items-center d-flex justify-content-between text-sm text-gray mt-auto">
        <span className="mr-2">{displayName}</span>
        {date && <span className="review-date ml-auto">{date}</span>}
      </div>
    </div>
  );
};

export default CustomerReviewCard;

